<template>
  <div class="lp-page about-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ $t('about.hero.badge') || 'About Us' }}</span>
        <h1 class="lp-title">{{ $t('about.hero.title') }}</h1>
        <p class="lp-lead">{{ $t('about.hero.subtitle') }}</p>
      </div>
    </section>

    <!-- Stats -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <div class="lp-grid lp-grid-4 stats-grid">
          <div class="stat">
            <span class="lp-ico blue"><font-awesome-icon icon="fa-solid fa-users" /></span>
            <div class="stat-num">100+</div>
            <div class="stat-label">{{ $t('about.stats.users') || 'Happy Users' }}</div>
          </div>
          <div class="stat">
            <span class="lp-ico green"><font-awesome-icon icon="fa-solid fa-file-invoice" /></span>
            <div class="stat-num">1M+</div>
            <div class="stat-label">{{ $t('about.stats.invoices') || 'Invoices Processed' }}</div>
          </div>
          <div class="stat">
            <span class="lp-ico violet"><font-awesome-icon icon="fa-solid fa-bullseye" /></span>
            <div class="stat-num">99.5%</div>
            <div class="stat-label">{{ $t('about.stats.accuracy') || 'Accuracy Rate' }}</div>
          </div>
          <div class="stat">
            <span class="lp-ico amber"><font-awesome-icon icon="fa-solid fa-headset" /></span>
            <div class="stat-num">24/7</div>
            <div class="stat-label">{{ $t('about.stats.support') || 'Support Available' }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Story -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="story-grid">
          <div class="story-image">
            <div class="story-visual">
              <div class="journey-card">
                <div class="jc-head">
                  <span class="jc-logo"><img src="/img/logo_quare.svg" alt="" /></span>
                  <div class="jc-head-txt"><b>{{ $t('about.journey.title') }}</b><i>{{ $t('about.journey.subtitle') }}</i></div>
                </div>
                <div class="jc-timeline">
                  <span class="jc-line"></span>
                  <div class="jc-item i1"><span class="jc-dot"></span><div class="jc-text"><b>2024</b><span>{{ $t('about.journey.t1') }}</span></div></div>
                  <div class="jc-item i2"><span class="jc-dot"></span><div class="jc-text"><b>{{ $t('about.journey.l2') }}</b><span>{{ $t('about.journey.t2') }}</span></div></div>
                  <div class="jc-item i3"><span class="jc-dot"></span><div class="jc-text"><b>{{ $t('about.journey.l3') }}</b><span>{{ $t('about.journey.t3') }}</span></div></div>
                  <div class="jc-item i4"><span class="jc-dot green"></span><div class="jc-text"><b>{{ $t('about.journey.l4') }}</b><span>{{ $t('about.journey.t4') }}</span></div></div>
                </div>
              </div>
              <div class="ex-chip ex-found"><span class="ex-ico violet"><font-awesome-icon icon="fa-solid fa-flag" /></span><div><b>Est. 2024</b><i>{{ $t('about.journey.byExperts') }}</i></div></div>
              <div class="ex-chip ex-rate"><span class="ex-ico amber"><font-awesome-icon icon="fa-solid fa-star" /></span><div><b>5.0 ★</b><i>{{ $t('about.journey.ratedByClients') }}</i></div></div>
            </div>
          </div>
          <div class="story-content">
            <span class="lp-eyebrow">{{ $t('about.story.badge') || 'Our Story' }}</span>
            <h2 class="lp-h2">{{ $t('about.story.title') }}</h2>
            <p class="lp-text story-text">{{ $t('about.story.description') }}</p>
            <ul class="story-highlights">
              <li><span class="hl-tick"><font-awesome-icon icon="fa-solid fa-check" /></span>{{ $t('about.story.highlight1') || 'Founded by industry experts' }}</li>
              <li><span class="hl-tick"><font-awesome-icon icon="fa-solid fa-check" /></span>{{ $t('about.story.highlight2') || 'Powered by cutting-edge AI' }}</li>
              <li><span class="hl-tick"><font-awesome-icon icon="fa-solid fa-check" /></span>{{ $t('about.story.highlight3') || 'Trusted by businesses worldwide' }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Mission & Values -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-section-head">
          <span class="lp-eyebrow">{{ $t('about.mission.badge') || 'Our Mission' }}</span>
          <h2 class="lp-h2">{{ $t('about.mission.title') }}</h2>
          <p class="lp-lead">{{ $t('about.mission.subtitle') }}</p>
        </div>
        <div class="lp-grid lp-grid-3">
          <div class="lp-card">
            <span class="lp-ico blue"><font-awesome-icon icon="fa-solid fa-cube" /></span>
            <h3 class="lp-h3">{{ $t('about.mission.innovation') }}</h3>
            <p class="lp-text">{{ $t('about.mission.innovationDesc') }}</p>
          </div>
          <div class="lp-card">
            <span class="lp-ico green"><font-awesome-icon icon="fa-solid fa-bolt" /></span>
            <h3 class="lp-h3">{{ $t('about.mission.efficiency') }}</h3>
            <p class="lp-text">{{ $t('about.mission.efficiencyDesc') }}</p>
          </div>
          <div class="lp-card">
            <span class="lp-ico violet"><font-awesome-icon icon="fa-solid fa-shield-halved" /></span>
            <h3 class="lp-h3">{{ $t('about.mission.reliability') }}</h3>
            <p class="lp-text">{{ $t('about.mission.reliabilityDesc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-section-head">
          <span class="lp-eyebrow">{{ $t('about.team.badge') || 'Our Team' }}</span>
          <h2 class="lp-h2">{{ $t('about.team.title') || 'Meet the Experts' }}</h2>
          <p class="lp-lead">{{ $t('about.team.subtitle') || 'Passionate professionals dedicated to transforming invoice processing' }}</p>
        </div>
        <div class="lp-grid lp-grid-3">
          <div class="lp-card team-card">
            <span class="team-avatar blue"><font-awesome-icon icon="fa-solid fa-code" /></span>
            <h3 class="lp-h3">{{ $t('about.team.member1.name') || 'Engineering Team' }}</h3>
            <p class="team-role">{{ $t('about.team.member1.role') || 'Technology & Development' }}</p>
            <p class="lp-text">{{ $t('about.team.member1.bio') || 'Building cutting-edge AI solutions for invoice processing' }}</p>
          </div>
          <div class="lp-card team-card">
            <span class="team-avatar green"><font-awesome-icon icon="fa-solid fa-pen-ruler" /></span>
            <h3 class="lp-h3">{{ $t('about.team.member2.name') || 'Product Team' }}</h3>
            <p class="team-role">{{ $t('about.team.member2.role') || 'Product & Design' }}</p>
            <p class="lp-text">{{ $t('about.team.member2.bio') || 'Creating intuitive experiences for seamless workflows' }}</p>
          </div>
          <div class="lp-card team-card">
            <span class="team-avatar violet"><font-awesome-icon icon="fa-solid fa-headset" /></span>
            <h3 class="lp-h3">{{ $t('about.team.member3.name') || 'Support Team' }}</h3>
            <p class="team-role">{{ $t('about.team.member3.role') || 'Customer Success' }}</p>
            <p class="lp-text">{{ $t('about.team.member3.bio') || 'Ensuring your success with dedicated support' }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-cta">
          <h2>{{ $t('about.cta.title') }}</h2>
          <p>{{ $t('about.cta.subtitle') }}</p>
          <div class="cta-buttons">
            <a href="/auth/google" class="lp-btn lp-btn-primary">{{ $t('about.cta.button') }}</a>
            <a href="/pricing" class="lp-btn lp-btn-secondary cta-secondary">{{ $t('pricing.viewPricing') || 'View Pricing' }}</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import Navbar from '../components/Navbar.vue';
import Footer from '../components/Footer.vue';
import { useReveal } from '../composables/useReveal';

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' });
});

useReveal();
</script>

<style scoped>
/* page-specific bits — the rest comes from the shared .lp-page theme */
.stats-grid { gap: 16px; }
.stat {
  background: var(--card); border: 1px solid var(--line); border-radius: var(--radius-sm);
  padding: 30px 24px; text-align: center; box-shadow: 0 26px 60px -38px rgba(31,41,51,0.32);
}
.stat .lp-ico { margin: 0 auto 16px; }
.stat-num { font-size: 34px; font-weight: 700; letter-spacing: -0.03em; color: var(--ink); }
.stat-label { font-size: 14px; color: var(--muted); margin-top: 4px; }

.story-grid { display: grid; grid-template-columns: 1fr 1.05fr; gap: 56px; align-items: center; }
/* ===== animated "invoice scanner" visual ===== */
.story-visual {
  position: relative; display: flex; align-items: center; justify-content: center;
  padding: 30px 26px;
}
/* animated "our journey" timeline — builds itself, dots gently pulse */
.journey-card {
  position: relative; width: 100%; max-width: 420px;
  background: #fff; border: 1px solid var(--line); border-radius: 22px;
  padding: 32px 32px 28px; box-shadow: 0 40px 80px -34px rgba(31,41,51,0.5);
}
.jc-head { display: flex; align-items: center; gap: 14px; padding-bottom: 22px; border-bottom: 1px solid var(--line); margin-bottom: 26px; }
.jc-logo { width: 50px; height: 50px; border-radius: 13px; background: #f4f7fc; border: 1px solid var(--line); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.jc-logo img { width: 30px; height: 30px; }
.jc-head-txt b { display: block; font-size: 18px; font-weight: 800; color: var(--ink); }
.jc-head-txt i { font-style: normal; font-size: 13.5px; color: var(--muted); }

.jc-timeline { position: relative; padding-left: 34px; }
.jc-line { position: absolute; left: 8px; top: 9px; bottom: 9px; width: 3px; border-radius: 3px; background: linear-gradient(180deg, #5aa2ff, #7c5cff); transform-origin: top; transform: scaleY(0); animation: jcLine 1.8s cubic-bezier(0.6,0,0.2,1) 0.2s forwards; }
.jc-item { position: relative; margin-bottom: 24px; opacity: 0; transform: translateX(10px); animation: jcItem 0.55s cubic-bezier(0.22,0.7,0.2,1) forwards; }
.jc-item:last-child { margin-bottom: 0; }
.jc-item.i1 { animation-delay: 0.5s; } .jc-item.i2 { animation-delay: 0.9s; } .jc-item.i3 { animation-delay: 1.3s; } .jc-item.i4 { animation-delay: 1.7s; }
.jc-dot { position: absolute; left: -34px; top: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; border: 3px solid var(--violet); animation: jcPulse 2.6s ease-in-out infinite; }
.jc-item.i2 .jc-dot { animation-delay: 0.5s; } .jc-item.i3 .jc-dot { animation-delay: 1s; } .jc-item.i4 .jc-dot { animation-delay: 1.5s; }
.jc-dot.green { border-color: #10b981; animation-name: jcPulseG; }
.jc-text b { display: block; font-size: 16px; font-weight: 800; color: var(--ink); letter-spacing: -0.01em; }
.jc-text span { font-size: 14px; color: var(--muted); line-height: 1.45; }
@keyframes jcLine { to { transform: scaleY(1); } }
@keyframes jcItem { to { opacity: 1; transform: none; } }
@keyframes jcPulse { 0%,100% { box-shadow: 0 0 0 4px rgba(43,130,240,0.12); } 50% { box-shadow: 0 0 0 8px rgba(43,130,240,0.03); } }
@keyframes jcPulseG { 0%,100% { box-shadow: 0 0 0 4px rgba(16,185,129,0.14); } 50% { box-shadow: 0 0 0 8px rgba(16,185,129,0.03); } }

/* floating milestone chips */
.ex-chip { position: absolute; display: flex; align-items: center; gap: 10px; background: #fff; border: 1px solid var(--line); border-radius: 14px; padding: 9px 13px; box-shadow: 0 18px 40px -22px rgba(31,41,51,0.45); animation: floaty 5s ease-in-out infinite; }
.ex-chip b { display: block; font-size: 14px; font-weight: 700; color: var(--ink); line-height: 1.1; }
.ex-chip i { font-style: normal; font-size: 11px; color: var(--muted); }
.ex-ico { width: 30px; height: 30px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; flex-shrink: 0; }
.ex-ico.amber { background: #f59e0b; } .ex-ico.green { background: #10b981; } .ex-ico.violet { background: #7c5cff; }
.ex-found { top: 16px; right: -10px; animation-delay: -1.2s; }
.ex-rate { bottom: 28px; right: -14px; animation-delay: -3s; }
@keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }

@media (prefers-reduced-motion: reduce) {
  .jc-line, .jc-item, .jc-dot, .ex-chip { animation: none !important; }
  .jc-line { transform: scaleY(1); }
  .jc-item { opacity: 1; transform: none; }
}
@media (max-width: 1024px) {
  .story-visual { padding: 24px 16px; }
}
.story-content .lp-h2 { margin: 4px 0 16px; }
.story-text { margin-bottom: 26px; font-size: 16px; }
.story-highlights { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.story-highlights li { display: flex; align-items: center; gap: 12px; font-size: 15.5px; font-weight: 600; color: var(--ink); }
.hl-tick {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
  background: rgba(43,130,240,0.12); color: var(--violet);
  display: inline-flex; align-items: center; justify-content: center; font-size: 12px;
}

.team-card { text-align: center; }
.team-card .lp-h3 { margin-bottom: 4px; }
.team-avatar {
  width: 72px; height: 72px; border-radius: 20px; margin: 0 auto 18px;
  display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 28px;
}
.team-avatar.blue   { background: linear-gradient(150deg, #5aa2ff, #2b82f0); }
.team-avatar.green  { background: linear-gradient(150deg, #34d399, #10b981); }
.team-avatar.violet { background: linear-gradient(150deg, #a78bfa, #7c5cff); }
.team-role { color: var(--violet); font-weight: 600; font-size: 14px; margin: 0 0 10px; }

.cta-buttons { display: inline-flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
.cta-secondary { background: rgba(255,255,255,0.1); color: #fff; border-color: rgba(255,255,255,0.25); }
.cta-secondary:hover { background: rgba(255,255,255,0.18); color: #fff; border-color: rgba(255,255,255,0.4); }

@media (max-width: 1024px) {
  .story-grid { grid-template-columns: 1fr; gap: 32px; }
}
</style>
